import { useConfirmationDialogContext } from '@/components/ui/ConfirmDialog.Context.tsx';
import { DialogVariant } from '@/components/ui/ConfirmDialog.tsx';

interface DialogOptions {
  title: string;
  description: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
}

interface GenericDialogOptions extends DialogOptions {
  variant?: DialogVariant;
}

// Callback-based options (new preferred way)
interface CallbackDialogOptions {
  title: string;
  description: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  loadingText?: string;
  onConfirm: () => Promise<void> | void;
}

export const confirmDialogUseHook = () => {
  const { showDialog, showDialogWithCallback } = useConfirmationDialogContext();

  const confirmDeleteWithCallback = (options: CallbackDialogOptions): void => {
    showDialogWithCallback({
      ...options,
      variant: 'destructive',
    });
  };

  const showInfoWithCallback = (options: CallbackDialogOptions): void => {
    showDialogWithCallback({
      ...options,
      variant: 'info',
    });
  };

  const confirmWithCallback = (options: CallbackDialogOptions): void => {
    showDialogWithCallback({
      ...options,
      variant: 'neutral',
    });
  };

  const confirmDelete = (options: DialogOptions): Promise<boolean> => {
    return showDialog({
      ...options,
      variant: 'destructive',
    });
  };

  const showInfo = (options: DialogOptions): Promise<true> => {
    return showDialog({
      ...options,
      variant: 'info',
    }) as Promise<true>; // Info dialog always returns true since there's no cancel
  };

  const confirm = (options: DialogOptions): Promise<boolean> => {
    return showDialog({
      ...options,
      variant: 'neutral',
    });
  };

  const showCustomDialog = (
    options: GenericDialogOptions,
  ): Promise<boolean> => {
    return showDialog(options);
  };

  return {
    // Callback-based API (preferred)
    confirmDeleteWithCallback,
    showInfoWithCallback,
    confirmWithCallback,

    // Promise-based API
    confirmDelete,
    showInfo,
    confirm,
    showDialog: showCustomDialog,
  };
};
